import { getIdleSessionPolicy } from './idleSessionConfig';

/** Intentional use of the app — not mouse-move, scroll, or background API. */
const ACTIVITY_EVENTS = ['click', 'keydown', 'mousedown', 'pointerdown', 'touchstart'];

let warningTimerId = null;
let expireTimerId = null;
let remainingTickId = null;
let lastActivityAt = 0;
let started = false;
let warningVisible = false;
let visibilityHandler = null;
let historyPatched = false;
let origPushState = null;
let origReplaceState = null;
let handlers = {
  onWarning: null,
  onExpire: null,
  onActivity: null,
};

const remainingListeners = new Set();
const THROTTLE_MS = 400;

function clearScheduleTimers() {
  if (warningTimerId != null) {
    window.clearTimeout(warningTimerId);
    warningTimerId = null;
  }
  if (expireTimerId != null) {
    window.clearTimeout(expireTimerId);
    expireTimerId = null;
  }
}

export function getIdleRemainingMs() {
  if (!started) {
    return getIdleSessionPolicy().idleTimeoutMs;
  }
  const { idleTimeoutMs } = getIdleSessionPolicy();
  return Math.max(0, idleTimeoutMs - (Date.now() - lastActivityAt));
}

export function formatIdleRemaining(ms) {
  const totalSeconds = Math.max(0, Math.ceil(Number(ms) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function notifyRemainingListeners() {
  const remainingMs = getIdleRemainingMs();
  remainingListeners.forEach((listener) => {
    try {
      listener(remainingMs);
    } catch {
      /* ignore listener errors */
    }
  });
}

export function subscribeIdleRemaining(listener) {
  if (typeof listener !== 'function') {
    return () => {};
  }
  remainingListeners.add(listener);
  listener(getIdleRemainingMs());
  return () => remainingListeners.delete(listener);
}

function fireExpire() {
  warningVisible = false;
  clearScheduleTimers();
  notifyRemainingListeners();
  if (typeof handlers.onExpire === 'function') {
    handlers.onExpire();
  }
}

function scheduleFromNow() {
  clearScheduleTimers();
  if (!started) return;

  const { idleTimeoutMs, warningLeadMs } = getIdleSessionPolicy();
  const remainingMs = getIdleRemainingMs();
  const lead = Math.max(1000, warningLeadMs);
  const warnAfter = Math.max(0, remainingMs - lead);

  warningTimerId = window.setTimeout(() => {
    warningVisible = true;
    if (expireTimerId != null) {
      window.clearTimeout(expireTimerId);
    }
    expireTimerId = window.setTimeout(() => {
      fireExpire();
    }, Math.max(0, getIdleRemainingMs()));

    if (typeof handlers.onWarning === 'function') {
      handlers.onWarning({
        remainingMs: getIdleRemainingMs(),
        idleTimeoutMs,
      });
    }
  }, warnAfter);

  expireTimerId = window.setTimeout(() => {
    fireExpire();
  }, remainingMs);

  notifyRemainingListeners();
}

function noteActivity(source = 'ui') {
  if (!started) return;
  const now = Date.now();
  if (source !== 'continue' && now - lastActivityAt < THROTTLE_MS) return;
  lastActivityAt = now;
  warningVisible = false;
  if (typeof handlers.onActivity === 'function') {
    handlers.onActivity({ source, at: now });
  }
  scheduleFromNow();
}

function onDomActivity() {
  noteActivity('dom');
}

function onNavigate() {
  noteActivity('navigate');
}

function patchHistoryForIdle() {
  if (historyPatched || typeof window === 'undefined' || !window.history) {
    return;
  }
  historyPatched = true;
  origPushState = window.history.pushState.bind(window.history);
  origReplaceState = window.history.replaceState.bind(window.history);
  window.history.pushState = function idlePushState(...args) {
    const result = origPushState(...args);
    onNavigate();
    return result;
  };
  window.history.replaceState = function idleReplaceState(...args) {
    const result = origReplaceState(...args);
    onNavigate();
    return result;
  };
}

export function notifyIdleActivity(source = 'ui') {
  if (source === 'api' || source === 'api-request' || source === 'api-response') {
    return;
  }
  noteActivity(source);
}

export function isIdleWarningVisible() {
  return warningVisible;
}

export function getLastIdleActivityAt() {
  return lastActivityAt;
}

export function startIdleSessionMonitor({ onWarning, onExpire, onActivity } = {}) {
  if (typeof window === 'undefined') return;

  handlers = {
    onWarning: onWarning || null,
    onExpire: onExpire || null,
    onActivity: onActivity || null,
  };

  if (started) {
    return;
  }

  started = true;
  lastActivityAt = Date.now();
  warningVisible = false;

  ACTIVITY_EVENTS.forEach((evt) => {
    window.addEventListener(evt, onDomActivity, { passive: true, capture: true });
  });
  window.addEventListener('popstate', onNavigate);

  visibilityHandler = () => {
    notifyRemainingListeners();
  };
  document.addEventListener('visibilitychange', visibilityHandler);

  patchHistoryForIdle();
  remainingTickId = window.setInterval(notifyRemainingListeners, 250);
  scheduleFromNow();
}

export function stopIdleSessionMonitor() {
  if (typeof window === 'undefined') return;
  started = false;
  warningVisible = false;
  clearScheduleTimers();
  if (remainingTickId != null) {
    window.clearInterval(remainingTickId);
    remainingTickId = null;
  }
  ACTIVITY_EVENTS.forEach((evt) => {
    window.removeEventListener(evt, onDomActivity, { capture: true });
  });
  window.removeEventListener('popstate', onNavigate);
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
  handlers = { onWarning: null, onExpire: null, onActivity: null };
  notifyRemainingListeners();
}

/** Continue session: hide warning and restart idle timers. */
export function continueIdleSession() {
  warningVisible = false;
  noteActivity('continue');
}
